import {
  createContext,
  useContext,
  type ParentComponent,
  createSignal,
  onMount,
} from "solid-js";
import type { ProfileResponse } from "../types/auth";
import { getProfile } from "../services/auth";
import { getToken } from "../lib/auth";
import { refreshColorFromLocalStorage } from "src/lib/useColor";

type AuthState = {
  user: ProfileResponse | null;
  loading: boolean;
};

type AuthActions = {
  setUser: (u: ProfileResponse) => void;
  clearUser: () => void;
  setLoading: (l: boolean) => void;
};

type AuthContextType = [AuthState, AuthActions];

const STORAGE_KEY = "sso-user";
const AuthContext = createContext<AuthContextType>();

export const AuthProvider: ParentComponent = (props) => {
  // init state dari localStorage kalau ada
  const stored = localStorage.getItem(STORAGE_KEY);
  const initialUser: ProfileResponse | null = stored
    ? JSON.parse(stored)
    : null;

  const [user, setUserSignal] = createSignal<ProfileResponse | null>(
    initialUser
  );
  const [loading, setLoadingSignal] = createSignal(false);

  const actions: AuthActions = {
    setUser: (u) => {
      setUserSignal(u);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(u));
    },
    clearUser: () => {
      setUserSignal(null);
      setLoadingSignal(false);
      localStorage.removeItem(STORAGE_KEY);
    },
    setLoading: (l) => setLoadingSignal(l),
  };

  const state: AuthState = {
    get user() {
      return user();
    },
    get loading() {
      return loading();
    },
  };

  onMount(async () => {
    if (!getToken()) {
      actions.clearUser();
      return;
    }
    const profile = await getProfile(true);
    actions.setUser(profile);
    refreshColorFromLocalStorage();
  });

  return (
    <AuthContext.Provider value={[state, actions]}>
      {props.children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
